import type { Workspace } from '@sr-sdks/api-sdk-axios';
import type { PropsWithChildren } from 'react';
import ErrorPage from '../components/ErrorPage';
import { LoadingFallback } from '../components/QueryResults/LoadingFallback';
import { SingleQueryResults } from '../components/QueryResults/SingleQueryResults';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { RequireCurrentMember } from './RequireCurrentMember';

/** Workspace-addon predicate from `utils/helpers/common` (e.g. `hasRoyaltiesEnabled`). */
type AddonPredicate = (workspace?: Workspace | null) => boolean;

type RequireAddonEnabledProps = PropsWithChildren<{
  isEnabled: AddonPredicate;
}>;

/**
 * Route guard for the workspace-addon half of an access rule. Workspaces the
 * predicate rejects see not-found (same outcome as a missing route).
 *
 * Prefer {@link RequireAddonAccess}, which also requires Current Member — the
 * addon check alone reads whatever workspace is cached.
 */
export const RequireAddonEnabled = ({
  children,
  isEnabled,
}: RequireAddonEnabledProps) => {
  const [workspace, activeWorkspaceQuery] = useActiveWorkspace();

  return (
    <SingleQueryResults query={activeWorkspaceQuery}>
      {() => {
        // keepPreviousData hands back the last workspace while a new one
        // loads; judging its addons would grant or deny the wrong workspace.
        if (activeWorkspaceQuery.isPlaceholderData) return <LoadingFallback />;

        return isEnabled(workspace) ? <>{children}</> : <ErrorPage notFound />;
      }}
    </SingleQueryResults>
  );
};

/**
 * Current Member **plus** a workspace addon.
 *
 * `RequireCurrentMember` must stay outermost: it is what triggers
 * `ensureFreshWorkspace` (and holds the loader while it settles) for users
 * without `ViewMemberships`. Deciding the addon half first would read a stale
 * `workspace.addons` and paint not-found on an entitled page — and short-circuit
 * past `RequireCurrentMember`'s refresh-error retry branch, making that 404
 * stick. See docs/payments/terminology-and-data-mapping.md (Access policy).
 */
export const RequireAddonAccess = ({
  children,
  isEnabled,
}: RequireAddonEnabledProps) => (
  <RequireCurrentMember>
    <RequireAddonEnabled isEnabled={isEnabled}>{children}</RequireAddonEnabled>
  </RequireCurrentMember>
);
